import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Settings, CalendarDays, ExternalLink, Sparkles } from "lucide-react";

/**
 * UniversalPreferenceCardPopup - the single canonical preference card UI
 * shown wherever a host or admin opens a client's card (Kanban, Clients table,
 * shared dashboards, host preview).
 *
 * Layout (top → bottom):
 *  1) Inspo strip     - 3-cell collage from the client's intake inspo. Cells with
 *                        no image fall back to the category light hex placeholder.
 *  2) Header row      - avatar · client name · service title · gear icon
 *  3) Tag row         - preferences synced from host-toggled fields (universal +
 *                        category specific). Hidden if host has not toggled any.
 *  4) Linked products - chips with brand · product · shade (read-only here).
 *  5) Final looks     - uploaded photo strip.
 *  6) Occasion        - name + date (when card came from a planned occasion).
 *  7) Date footer     - calendar icon + appointment date.
 *
 * Every section auto-hides when its data array is empty so the card stays
 * compact for new clients.
 */
export function UniversalPreferenceCardPopup({
  open,
  onOpenChange,
  client,                    // { name, avatar_url, service, occasion, date }
  inspo = [],                // [url, ...] up to 3 visible
  tagLabels = [],            // ["normal", "prom glam", ...]
  linkedProducts = [],       // [{ brand, name, shade, image_url }, ...]
  finalLooks = [],           // [url, ...]
  categoryLightHex = "#f5f5f4",
  testIdPrefix = "pref-card",
}) {
  const inspoCells = [0, 1, 2].map(i => inspo[i] || null);
  const initial = (client?.name || "C").charAt(0).toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid={`${testIdPrefix}-popup`}>
        <DialogHeader className="sr-only">
          <DialogTitle>{client?.name || "Preference card"}</DialogTitle>
        </DialogHeader>

        {/* 1) Inspo strip */}
        <div className="grid grid-cols-3 gap-px bg-stone-100" data-testid={`${testIdPrefix}-inspo`}>
          {inspoCells.map((src, i) => (
            <div key={i} className="aspect-[4/5] overflow-hidden" style={{ backgroundColor: categoryLightHex }}>
              {src && <img src={src} alt="inspo" className="w-full h-full object-cover" />}
            </div>
          ))}
        </div>

        {/* 2) Header row */}
        <div className="p-4 flex items-center gap-3" data-testid={`${testIdPrefix}-header`}>
          <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center text-sm font-semibold shrink-0 overflow-hidden">
            {client?.avatar_url ? (
              <img src={client.avatar_url} alt={client?.name} className="w-full h-full object-cover" />
            ) : (
              initial
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-stone-900 truncate">{client?.name || "-"}</p>
            {client?.service && (
              <p className="text-xs text-stone-400 truncate">{client.service}</p>
            )}
          </div>
          <Settings className="w-4 h-4 text-stone-400" />
        </div>

        {/* 3) Tag row */}
        {tagLabels.length > 0 && (
          <div className="px-4 pb-3 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-tags`}>
            {tagLabels.map((t, i) => (
              <span key={i} className="text-[11px] px-2.5 py-0.5 rounded-full bg-stone-100 text-stone-700">{t}</span>
            ))}
          </div>
        )}

        {/* 4) Linked products */}
        {linkedProducts.length > 0 && (
          <div className="px-4 pb-3 border-t border-stone-100 pt-3" data-testid={`${testIdPrefix}-products`}>
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Linked products</p>
            <div className="space-y-1.5">
              {linkedProducts.slice(0, 5).map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className="w-7 h-7 rounded bg-stone-50 border border-stone-100 overflow-hidden flex items-center justify-center shrink-0">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <Sparkles className="w-3 h-3 text-stone-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-stone-800 truncate">{p.name}</p>
                    <p className="text-[10px] text-stone-400 truncate">
                      {p.brand}{p.shade ? ` · ${p.shade}` : ""}
                    </p>
                  </div>
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-stone-700">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 5) Final looks */}
        {finalLooks.length > 0 && (
          <div className="px-4 pb-3 border-t border-stone-100 pt-3" data-testid={`${testIdPrefix}-final-looks`}>
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Final looks</p>
            <div className="grid grid-cols-3 gap-1.5">
              {finalLooks.slice(0, 6).map((src, i) => (
                <div key={i} className="aspect-square rounded-md overflow-hidden border border-stone-100">
                  <img src={src} alt={`final-look-${i}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6) Occasion */}
        {client?.occasion && (
          <div className="px-4 pb-3 border-t border-stone-100 pt-3 text-xs text-stone-600" data-testid={`${testIdPrefix}-occasion`}>
            <span className="text-[10px] uppercase tracking-wider text-stone-500 mr-2">Occasion</span>
            {client.occasion}
          </div>
        )}

        {/* 7) Date footer */}
        {client?.date && (
          <div className="px-4 pb-4 pt-3 border-t border-stone-100 flex items-center gap-1.5 text-xs text-stone-500" data-testid={`${testIdPrefix}-date`}>
            <CalendarDays className="w-3.5 h-3.5" />
            <span>{(() => {
              const d = new Date(client.date);
              if (isNaN(d.getTime())) return String(client.date);
              return d.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
            })()}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UniversalPreferenceCardPopup;
